<template>
  <!-- ── Icon mode ─────────────────────────────────────────────────────── -->
  <template v-if="mode === 'icon'">
    <!-- A camera with a stream keeps the live picture — the video IS the pin's
         content (P14); a camera with no stream draws the pin's own camera glyph
         inside the status ring's bare track (the camera carries no reading to
         put on the arc). -->
    <div v-if="cameraId" class="relative w-full h-full overflow-hidden rounded-lg">
      <CameraWidget
        :camera-id="cameraId"
        :camera-name="displayName"
        :snapshot="snapshot"
        :is-connected="device.enabled"
        :hide-footer="true"
        :hide-controls="true"
        :unstyled="true"
        @click.stop />
    </div>
    <div v-else class="device-icon-pin" :class="sizeClass" :title="device.deviceName">
      <ControlStatusRing :value="null">
        <ControlIcon name="camera" :size="pinIconSize" />
      </ControlStatusRing>
    </div>
  </template>

  <!-- ── Card mode ───────────────────────────────────────────────────── -->
  <template v-else-if="mode === 'card'">
    <!-- Card face: the camera's own picture in the media box, its name, and
         whether it is online; the largest size offers the stream. -->
    <DeviceCardShell
      :size="cardSize ?? 'min'"
      :name="device.deviceName"
      :title="device.deviceName"
      :active="!!device.enabled"
      accent="rgb(34, 197, 94)"
      class="camera-card-face"
      @click="emit('open-control-modal')"
      @contextmenu.prevent="emit('open-control-modal')">
      <template #media>
        <!-- The picture fills the media box: the card face is the frame, so the
             widget drops its own card chrome (`unstyled`) the way the icon pin
             and the Cameras page already do. -->
        <CameraWidget
          v-if="cameraId"
          :camera-id="cameraId"
          :camera-name="displayName"
          :snapshot="snapshot"
          :is-connected="device.enabled"
          :unstyled="true"
          :hide-footer="true"
          :hide-controls="true"
          @click.stop />
        <span v-else class="material-symbols-outlined camera-card-face__glyph">{{ device.icon || 'videocam' }}</span>
      </template>

      <template v-if="cardSize !== 'min'" #value>
        <span class="camera-card-face__value">{{
          device.enabled ? tt('Online', 'Na voljo') : tt('Offline', 'Ni na voljo')
        }}</span>
      </template>

      <template v-if="cardSize === 'max'" #control>
        <button type="button" class="camera-card-face__verb" @click.stop="emit('open-control-modal')">
          <span class="material-symbols-outlined">open_in_full</span>
          {{ tt('Open camera', 'Odpri kamero') }}
        </button>
      </template>
    </DeviceCardShell>
  </template>

  <!-- ── Grid mode ───────────────────────────────────────────────────── -->
  <template v-else-if="mode === 'grid'">
    <DeviceGridCell
      :icon="device.icon || 'videocam'"
      :name="device.deviceName"
      :is-on="!!device.enabled"
      active-color="green-400"
      @toggle="emit('open-control-modal')"
      @long-press="emit('open-control-modal')" />
  </template>

  <!-- ── Control mode ──────────────────────────────────────────────────── -->
  <!-- The camera BODY is intentionally untouched (this card's own note): the
       live CameraWidget renders as it always has. Only its container speaks the
       sheet's language — the sheet radius, the video clipped into it, and the
       empty fallback on the same surface. -->
  <template v-else-if="mode === 'control'">
    <div class="camera-sheet">
      <CameraWidget
        v-if="cameraId"
        :camera-id="cameraId"
        :camera-name="device.deviceName"
        :snapshot="snapshot"
        :is-connected="device.enabled" />
      <div v-else class="camera-empty">
        <ControlIcon name="camera" size="2.25rem" class="camera-empty__glyph" />
        <div class="camera-empty__name">{{ device.deviceName }}</div>
      </div>
    </div>
  </template>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted } from 'vue';

import CameraWidget from '../CameraWidget.vue';
import DeviceCardShell from './DeviceCardShell.vue';
import DeviceGridCell from '../panel/DeviceGridCell.vue';
import ControlIcon from '../device-icons/ControlIcon.vue';
import ControlStatusRing from '../device-sheet/ControlStatusRing.vue';
import { useCameraSnapshotsStore } from '@/stores/cameraSnapshots';
import { tt } from '../../utils/consoleText';
import type { Device } from '@/types';

interface Props {
  device: Device;
  mode?: 'icon' | 'card' | 'control' | 'grid';
  size?: 'sm' | 'md' | 'lg';
  fluid?: boolean;
  displayMode?: 'full' | 'room';
  /** Set by the pages that grid cards (`DeviceButton`) — `undefined` keeps the
   *  compact button row other surfaces have always drawn. */
  cardSize?: 'min' | 'mid' | 'max';
}

const props = withDefaults(defineProps<Props>(), { mode: 'card', size: 'md', cardSize: undefined });

const emit = defineEmits<{
  toggle: [];
  'open-control-modal': [];
  'view-stream': [];
  'view-details': [];
  'value-update': [valueId: string, newValue: unknown];
}>();

// ── Camera snapshot ───────────────────────────────────────────────────────
const cameraSnapshotsStore = useCameraSnapshotsStore();

const cameraId = computed(() => props.device.camera?.id || props.device.endpoint?.protocolEndpointId || '');

const snapshot = computed(() => cameraSnapshotsStore.getSnapshotUrl(cameraId.value));

let snapshotInterval: ReturnType<typeof setInterval> | null = null;

/**
 * Batched through the store: every camera tile on a page mounts in the same
 * tick, and one request per tile saturated the connection pool (see
 * `cameraSnapshots.queueSnapshot`).
 */
function requestSnapshot() {
  if (!cameraId.value) return;
  cameraSnapshotsStore.queueSnapshot(cameraId.value);
}

onMounted(() => {
  requestSnapshot();
  snapshotInterval = setInterval(requestSnapshot, 30_000);
});

onUnmounted(() => {
  if (snapshotInterval) clearInterval(snapshotInterval);
});

// ── Display ──────────────────────────────────────────────────────────────
const displayName = computed(() =>
  props.displayMode === 'full' ? props.device.deviceName : props.device.shortName || props.device.deviceName,
);
const sizeClass = computed(() => (props.fluid ? 'device-icon-pin--fluid' : `device-icon-pin--${props.size ?? 'md'}`));

/** The drawn camera's size inside the pin (P14), in the `GenericIcon` steps. */
const pinIconSize = computed(() => {
  if (props.fluid) return '28px';
  switch (props.size) {
    case 'lg':
      return '28px';
    case 'sm':
      return '16px';
    default:
      return '22px';
  }
});
</script>

<style scoped>
/* ── Control mode: the sheet container ────────────────────────────────────
   The body inside is the live CameraWidget, unchanged; only the box follows the
   sheet's own radius (the widget's own card corners are clipped into it). */
.camera-sheet {
  display: flex;
  flex-direction: column;
  border-radius: calc(0.875rem * var(--ui-radius-factor));
  overflow: hidden;
}

.camera-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  padding: calc(2rem * var(--ui-space-factor)) 0;
  border-radius: calc(0.875rem * var(--ui-radius-factor));
  background: rgb(17, 24, 39);
}

.camera-empty__glyph {
  color: rgb(75, 85, 99);
}

.camera-empty__name {
  font-size: 0.75rem;
  font-weight: 500;
  color: rgb(156, 163, 175);
}

/* ── Card face ─────────────────────────────────────────────────────────────── */
.camera-card-face__glyph {
  font-size: 2.4rem;
}

.camera-card-face__value {
  flex: none;
  text-align: center;
  font-size: var(--card-value, 0.85rem);
  font-weight: 600;
  color: #64748b;
}

.dark .camera-card-face__value {
  color: #94a3b8;
}

.camera-card-face__verb {
  display: inline-flex;
  width: 100%;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  border-radius: 0.5rem;
  background: rgba(148, 163, 184, 0.16);
  padding: 0.3rem 0.4rem;
  font-size: 0.72rem;
  font-weight: 600;
  color: #475569;
}

.camera-card-face__verb .material-symbols-outlined {
  font-size: 1rem;
}

.dark .camera-card-face__verb {
  background: rgba(255, 255, 255, 0.08);
  color: #cbd5e1;
}
</style>
